import { Box, Button, Divider, FormControl, MenuItem, Select, Skeleton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { ActivityEnums } from "../../../../common/constants";
import { useDispatch } from "../providers/DispatchProvider";
import { useStation } from "../../../../providers/StationProvider";
import { useCallback } from "react";
import { MakeActivity } from "../../../../common/activity";
import { useNotification } from "../../../../providers/NotificationProvider";
import { useNavigate } from "react-router";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      position: "relative",
      background: theme.palette.background.default,
      padding: `${theme.spacing(1.75)} ${theme.spacing(1.5)}`,
      paddingRight: theme.spacing(2),
      borderRadius: theme.spacing(2),
    },
    button: {
      height: "100%"
    }
  })
);

export default function InfoToolbar() {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { station, updateStation, pushToDispatch, logEvent, permissions } = useStation();
  const { snackbar } = useNotification();

  const navigate = useNavigate();

  const {
    activeDrivers,
    operators,
    completedTasks,
    completedTasks24H,
    unassignedDrivers
  } = useDispatch();

  const onChangeOperator = useCallback(async (ev) => {
    const id = ev.target.value;
    const operator = operators.find(e => e.id === id);

    if (id !== -1 && !!operator) {

      updateStation({
        dispatcherOnDuty: { id: operator.id, name: operator.name }
      }).then(() => {
        snackbar(t("operators:notifications:setOnDuty"));
        pushToDispatch({
          title: t("notification:dispatcherOnDutyNotificationTitle"),
          message: `${t("notification:dispatcherOnDutyNotificationDescription")}`,
        });
        logEvent(MakeActivity(ActivityEnums.Category.Other, ActivityEnums.Type.DispatcherOnDuty, operator.id, "", `${t("operators:item:log")} ${operator.name}`, { id: operator.id, name: operator.name }));
      });

    }
  }, [operators, updateStation, pushToDispatch, logEvent, snackbar, t]);

  return (
    <Box className={classes.root}>
      <Stack direction="row" spacing={1.5}>

        <Button className={classes.button} onClick={() => navigate(`./drivers`, { relative: "path" })}>
          <Stack spacing={0} justifyContent="center" alignItems="flex-start" >
            <Typography variant="caption" color="textSecondary">{t("dispatch:infoToolbar:drivers")}</Typography>
            <Typography variant="body2">{activeDrivers.length} {t("dispatch:infoToolbar:isActive")} • {unassignedDrivers.length} {t("dispatch:infoToolbar:isFree")}</Typography>
          </Stack>
        </Button>

        <Divider orientation="vertical" sx={{ height: "48px" }} />

        <Button className={classes.button} onClick={() => navigate(`./completed`, { relative: "path" })}>
          <Stack spacing={0} justifyContent="center" alignItems="flex-start">
            <Typography variant="caption" color="textSecondary">{t("dispatch:infoToolbar:recentlyClosed")}</Typography>
            <Typography variant="body2">{completedTasks24H.length} {t("dispatch:infoToolbar:last24h")} • {completedTasks.length} {t("common:today")}</Typography>
          </Stack>
        </Button>

        <Divider orientation="vertical" sx={{ height: "48px" }} />

        <Stack spacing={0} justifyContent="center" alignItems="flex-start">
          <Typography variant="caption" color="textSecondary" sx={{ marginTop: "2px" }}>{t("dispatch:infoToolbar:operator")}</Typography>
          {
            operators.length > 0 ? (
              <FormControl fullWidth size="small" variant="standard" className={classes.dispatcherOnDuty} disabled={!permissions?.canSetDispatcherOnDuty}>
                <Select
                  IconComponent={ExpandMoreIcon}
                  onChange={onChangeOperator}
                  value={station?.dispatcherOnDuty?.id || -1}
                  disableUnderline
                  sx={{
                    marginTop: "1px",
                    flexGrow: 1,
                    '& .MuiSelect-select': {
                      paddingTop: 0,
                      paddingBottom: 0,
                    },
                    '& .MuiSelect-icon': {
                      top: "-2px",
                      right: "-4px"
                    }
                  }}
                >
                  {!station?.dispatcherOnDuty?.id ?
                    <MenuItem value={-1}>
                      <Typography variant="body2">{t("dispatch:infoToolbar:noDispatcher")}</Typography>
                    </MenuItem>
                    : null}
                  {operators.map((operator, key) =>
                    <MenuItem value={operator.id} key={key}>
                      <Typography variant="body2">{operator.name}</Typography>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            ) : (
              <Skeleton variant="rounded" width="110px" height="22px" />
            )
          }
        </Stack>

      </Stack>
    </Box>
  );
}



